import React, { useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import CloudinaryPicker from "./CloudinaryPicker";

export default function BookingGalleryManager({ photos = [], onChange }) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const addPhotos = (assets) => {
    const selected = Array.isArray(assets) ? assets : [assets];
    onChange([
      ...photos,
      ...selected.map((asset, index) => ({
        url: asset.secure_url,
        public_id: asset.public_id,
        caption: "",
        order: photos.length + index,
      })),
    ]);
  };

  const removePhoto = (index) => {
    if (!window.confirm("Remove this photo?")) return;
    onChange(photos.filter((_, photoIndex) => photoIndex !== index));
  };

  const movePhoto = (index, direction) => {
    const next = [...photos];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next.map((photo, order) => ({ ...photo, order })));
  };

  const setCaption = (index, caption) => {
    onChange(photos.map((photo, photoIndex) => photoIndex === index ? { ...photo, caption } : photo));
  };

  return (
    <>
      <section className="bento" data-testid="landing-gallery-section">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="overline">▸ BOOKING GALLERY ({photos.length})</h3>
          <button type="button" onClick={() => setPickerOpen(true)} data-testid="landing-gallery-add"
            className="btn-primary inline-flex items-center gap-1.5 text-[12px]">
            <Plus size={13} /> Add photos
          </button>
        </div>
        <div className="mb-3 text-[11px]" style={{ color: "var(--gio-muted-3)" }}>
          Photos shown in the Book Me modal on giopilos.com. Upload as many as you want, drag-order with arrows.
        </div>
        {photos.length === 0 && (
          <div className="py-12 text-center text-[13px]" style={{ color: "var(--gio-muted-3)" }}>
            No photos yet. Click &quot;Add photos&quot; to upload or pick from library.
          </div>
        )}
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {photos.map((photo, index) => (
            <div key={photo.public_id || photo.url || index} className="overflow-hidden rounded-lg border"
              data-testid={`landing-gallery-item-${index}`} style={{ borderColor: "var(--gio-border)" }}>
              <div className="relative aspect-square">
                <img src={photo.url} alt={photo.caption || "Booking gallery"} className="h-full w-full object-cover" />
                <button type="button" onClick={() => removePhoto(index)} data-testid={`landing-gallery-remove-${index}`}
                  aria-label={`Remove gallery photo ${index + 1}`}
                  className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-black/70 hover:bg-red-600">
                  <Trash2 size={12} className="text-white" />
                </button>
                <div className="absolute left-1 top-1 flex flex-col gap-1">
                  <button type="button" onClick={() => movePhoto(index, -1)} disabled={index === 0}
                    data-testid={`landing-gallery-up-${index}`} aria-label={`Move photo ${index + 1} up`}
                    className="flex h-7 w-7 items-center justify-center rounded bg-black/70 disabled:opacity-30">
                    <ChevronUp size={12} className="text-white" />
                  </button>
                  <button type="button" onClick={() => movePhoto(index, 1)} disabled={index === photos.length - 1}
                    data-testid={`landing-gallery-down-${index}`} aria-label={`Move photo ${index + 1} down`}
                    className="flex h-7 w-7 items-center justify-center rounded bg-black/70 disabled:opacity-30">
                    <ChevronDown size={12} className="text-white" />
                  </button>
                </div>
              </div>
              <input value={photo.caption || ""} onChange={(event) => setCaption(index, event.target.value)}
                data-testid={`landing-gallery-caption-${index}`} aria-label={`Photo ${index + 1} caption`}
                placeholder="Caption (optional)" className="w-full bg-transparent px-2 py-1.5 text-[11px] outline-none"
                style={{ color: "var(--gio-text)" }} />
            </div>
          ))}
        </div>
      </section>
      {pickerOpen && (
        <CloudinaryPicker
          open
          onClose={() => setPickerOpen(false)}
          onPick={(assets) => {
            addPhotos(assets);
            setPickerOpen(false);
          }}
          multiple
          folderHint="landing/gallery"
        />
      )}
    </>
  );
}